import { Pressable, KeyboardAvoidingView, Platform, Keyboard, Alert, TouchableOpacity } from "react-native"
import { Formik, FormikProps, Field, setNestedObjectValues, FormikTouched, FormikHelpers, useField, FastField, FieldProps } from "formik";
import { Text, View } from "@constants/Themed";
import React, { JSX, forwardRef, memo, RefObject, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import styles from "@stylesheet";
import { ScrollView } from "@constants/Themed";
import useGlobalUser from "./getGlobalUser";
// import Button from "@components/UI/Button/Button";
import { Spinner, XStack } from 'tamagui'
import AuthModal from "@components/modals/ErrorModal/AuthModal";
import { FormValues } from "@components/Global/types";
import parseByDelimiter from "@utils/parseByDelimiter";
import generateYupValidation from "./generateYupValidation";
import * as Animatable from 'react-native-animatable';
import Row from './rowComp';
import { MotiPressable } from 'moti/interactions';
import { themeSelector } from "@reduxLocal/selectors/themeSelector";
import { LinearProgress } from "@constants/Themed";

const FormikDebugger = ({ status }) => {
  useEffect(() => {
    console.log("Formik State Changed:", {
      status
    });
  }, [status]);

  return null; // no visible UI
};

export interface SchemaObject {
  /**
   * @param schema - The schema object to be generated
   */
  schema: object,
  /**
   * @param staged - Whether the form is staged, introduce one field at a time
   */
  staged? : boolean,
  /**
   * @param submitFunction - The function to be called when the form is submitted
   */
  submitFunction?: Function,
  /**
   * @param nullify - The fields to be nullified
   */
  nullify?: Array<string>,
  /**
   * @param sheetRef - The reference to the bottom sheet
   */
  sheetRef?: RefObject<FormikHelpers<any>>,
  /**
   * @param other - The id of the data to be submitted, this should be the _id of the document
   */
  other?: any,
  /**
   * @param otherVals - The other values to be passed into special fields
   */
  otherVals?: {}
  /**
   * @param callback - The callback function to be called after submission
   * @requires state - The state to be updated after submission
   * @requires setState - The function to update the state
   */
  callback?: { state: boolean, setState: Function },
  /**
   * @param dispatch - The dispatch function to be called after submission
   */
  dispatch?: (_id: string)=> void,
  /**
   * @param internalButton - Whether the button is internal
   */
  internalButton?: boolean,
  /**
   * @param buttonLabel - The label of the button
   */
  buttonLabel?: string, // Make buttonLabel optional as it can be provided by renderFormControls
  buttonIcon?: string | JSX.Element,
  /**
   * @param buttonStyle - The style of the button
   */
  buttonStyle?: any,
  /**
   * @param avoidKeyboardDisabled - Whether the keyboard should be avoided
   * @description This is only applicable for mobile devices
   * @default false
   */
  avoidKeyboardDisabled?: boolean,
  /**
   * @param Progress - Whether to display the ProgressBar bar
   * @default false
   */
  Progress?: boolean,
  /**
   * @param noBackground - Whether to display the background
   * @default false
   */
  noBackground?: boolean,
  /**
   * @param retainInitialValues - Should passed values be kept after submission
   * @default false
   */
  retainInitialValues?: boolean,
  /**
   * @param initialData - The initial data passed to the form in the form of an object
   */
  initialData?: any,
  addOffset?: number,
  /**
   * @param hasTitle - Whether the schema has a title
   * @default false
   */
  hasTitle?: boolean,
  /**
   * @param columns - The number of columns to be displayed
   */
  columns?: number,
  /**
   * @param extraFunction - The extra function to be called after submission
   * @default null
   * */
  extraFunction?: Function,
  customStyling?: Object,
  addPadding?: boolean,
  textColor?: string,
  extraCallBack?: Function,
  schemaName?: string,
  extraId?: string,
  buttonColor?: string,
  /**
   * @param scrollEnabled - Whether the form should be scrollable
   * @default true
   */
  scrollEnabled?: boolean,
  /**
   * @param scrollBarVisible - Whether the scrollbar should be visible
   * @default true
   */
  scrollBarVisible?: boolean,
  hideButton?: boolean,
  removeSubmit?: boolean,
  bottomPadding?: number,
  containerWidth?: number,
  hideLabel?: boolean,
  /**
   * @param extraValues - The extra values to be passed to the submit function
   * @default {}
   */
  extraValues?: Object,
  blackText?: boolean,
  /**
   * @param resetOnSubmit - Whether to reset the form on submit
   * @default false
   */
  resetOnSubmit?: boolean,
  /**
   * @param endReached - Callback triggered when reaching the last question in staged mode
   * @param values - The current form values
   * @default undefined
   */
  endReached?: (values: any) => void,
  /**
   * @param showSkipButton - Whether to show a skip button
   * @default false
   */
  showSkipButton?: boolean,
  /**
   * @param skipButtonLabel - The label for the skip button
   * @default "Skip"
   */
  skipButtonLabel?: string,
  /**
   * @param onSkip - Callback function called when skip button is pressed
   * @default undefined
   */
  onSkip?: () => void,
  /**
   * @param readOnly - Whether the form should be in read-only mode (fields disabled/read-only)
   * @default false
   */
  readOnly?: boolean,
  /**
   * @param renderFormControls - A render function to provide custom form controls (e.g., submit button).
   * Takes FormikProps as an argument. If provided, the internal button will not be rendered.
   */
  renderFormControls?: (formikProps: FormikProps<any>) => React.ReactNode,
  /**
   * @param onCancel - Callback function called when cancel button is pressed
   * @default undefined
   */
  onCancel?: () => void,
  /**
   * @param cancelButtonLabel - The label for the cancel button
   * @default "Cancel"
   */
  cancelButtonLabel?: string,
  /**
   * @param inlineFields - Whether to render fields in an XStack with flexWrap="wrap"
   * @default false
   */
  inlineFields?: boolean,
}

// FormBody component - extracted from Formik render callback to fix Rules of Hooks violation
interface FormBodyProps {
  formikProps: {
    validateForm: any;
    resetForm: any;
    handleChange: any;
    setFormikState: any;
    isSubmitting: boolean;
    touched: any;
    setSubmitting: (value: boolean) => void;
    handleSubmit: any;
    submitForm: any;
    errors: any;
    values: any;
    status: any;
  };
  props: SchemaObject;
  schemaArray: any[];
  formInstanceId: string;
  currentQuestionIndex: number;
  setCurrentQuestionIndex: (index: number) => void;
  endReachedTriggered: React.MutableRefObject<boolean>;
  handleFocusNextField: any;
  setInputRef: any;
  getVisibleFields: (values: any) => any[];
  RenderStagedRows: any;
  RenderRows: any;
  isDevice: boolean;
  isTouchDeviceRef: React.MutableRefObject<boolean>;
  shouldPreventBlurRef: React.MutableRefObject<boolean>;
  focusTimeRef: React.MutableRefObject<number>;
  columns: number;
  inlineFields?: boolean;
}

const FormBody = React.memo(({
  formikProps,
  props,
  schemaArray,
  formInstanceId,
  currentQuestionIndex,
  setCurrentQuestionIndex,
  endReachedTriggered,
  handleFocusNextField,
  setInputRef,
  getVisibleFields,
  RenderStagedRows,
  RenderRows,
  isDevice,
  isTouchDeviceRef,
  shouldPreventBlurRef,
  focusTimeRef,
  columns,
  inlineFields
}: FormBodyProps) => {
  // Track renders for debugging
  const renderCountRef = useRef(0);
  const prevPropsRef = useRef<any>({});
  const prevFormikPropsRef = useRef<any>({});

  renderCountRef.current += 1;
  const renderCount = renderCountRef.current;

  // Log render with what changed
  useEffect(() => {
    const changedProps: string[] = [];
    const changedFormikProps: string[] = [];

    // Check props changes
    Object.keys(props).forEach(key => {
      if (prevPropsRef.current[key] !== props[key]) {
        changedProps.push(key);
      }
    });

    // Check formikProps changes
    Object.keys(formikProps).forEach(key => {
      if (prevFormikPropsRef.current[key] !== formikProps[key]) {
        changedFormikProps.push(key);
      }
    });

    if (changedProps.length > 0 || changedFormikProps.length > 0) {
      // Only log significant changes, not every render
      const significantChanges = changedFormikProps.filter(key =>
        key !== 'values' && key !== 'touched' // values and touched change on every keystroke
      );

      if (significantChanges.length > 0 || changedProps.length > 0 || renderCount === 1) {
        if (__DEV__) {
          console.log(`🔄 [FormBody] Render #${renderCount}`, {
            changedProps,
            changedFormikProps: significantChanges.length > 0 ? significantChanges : changedFormikProps,
            timestamp: new Date().toISOString(),
            valuesKeys: Object.keys(formikProps.values || {}),
            errorsKeys: Object.keys(formikProps.errors || {}),
            isSubmitting: formikProps.isSubmitting,
          });
        }
      }
    }

    prevPropsRef.current = { ...props };
    prevFormikPropsRef.current = { ...formikProps };
  });

  // Get theme - this is now in a proper component, so hooks are allowed
  const theme = themeSelector();

  const { validateForm, setFormikState, isSubmitting, setSubmitting, handleSubmit, errors, values, status } = formikProps;

  // For MUI LinearProgress, value should be between 0 and 100
  const handleUpdateProgressBar = () => {
    const visibleFields = getVisibleFields(values || {});
    if (!visibleFields?.length) return 0;
    const singleValue = 100 / visibleFields.length;
    let progressValue = 0;
    if (!values) return 0;

    // Only count progress for visible fields
    visibleFields.forEach(field => {
      const fieldKey = field.title || field.key;
      const value = values[fieldKey];
      if (value !== 'null' && !props.nullify?.includes(fieldKey)) {
        if (Array.isArray(value)) {
          if (value?.length >= 1) {
            progressValue += singleValue;
          }
        } else if (value != null && value !== '' && value) {
          progressValue += singleValue;
        }
      }
    });
    // Clamp between 0 and 100
    return Math.min(progressValue, 100);
  };

  // Memoize visible fields to prevent recalculation on every keystroke
  // Only recalculate when conditional dependencies change, not on every value change
  const memoizedVisibleFields = useMemo(() => {
    const visible = getVisibleFields(values || {});
    return visible;
  }, [
    // Build a comprehensive list of all field keys that affect conditional visibility
    // Extract dependent keys from schema
    (() => {
      const dependentKeys = new Set<string>();
      schemaArray.forEach(field => {
        // Check dependsOn format (backend): { field: 'fieldName', value: 'expectedValue' }
        const dependsOn = field.dependsOn || field.validation?.dependsOn;
        if (dependsOn && typeof dependsOn === 'object' && dependsOn.field) {
          dependentKeys.add(dependsOn.field);
        }
        // Check conditional format (frontend): { field: 'fieldName', operator: 'equals', value: 'expectedValue' }
        if (field.conditional && field.conditional.field) {
          dependentKeys.add(field.conditional.field);
        }
      });
      // Create dependency string from only dependent field values (not all values)
      // This prevents re-renders on every keystroke for non-dependent fields
      return Array.from(dependentKeys).map(key => {
        const val = values?.[key];
        // Use a stable representation that doesn't change unnecessarily
        if (val === null || val === undefined) return `${key}:null`;
        if (typeof val === 'boolean') return `${key}:${val}`;
        if (typeof val === 'string' || typeof val === 'number') return `${key}:${val}`;
        // For objects/arrays, use a hash or length to avoid full JSON.stringify
        if (Array.isArray(val)) return `${key}:array:${val.length}`;
        if (typeof val === 'object') return `${key}:object:${Object.keys(val).length}`;
        return `${key}:${String(val)}`;
      }).join('|');
    })(),
    schemaArray.length
  ]);

  // Trigger endReached callback when on the last question in staged mode
  useEffect(() => {
    if (props.staged && props.endReached && memoizedVisibleFields && memoizedVisibleFields.length > 0) {
      if (currentQuestionIndex === memoizedVisibleFields.length - 1 && !endReachedTriggered.current) {
        // We're on the last question and haven't triggered the callback yet
        endReachedTriggered.current = true;
        props.endReached(values || {});
      }
    }
  }, [currentQuestionIndex, memoizedVisibleFields?.length, props.staged, props.endReached, values]);

  // Don't wrap entire JSX in useMemo - just return it directly
  return (
    <View
      style={{ flex: 1, minHeight: 200, backgroundColor: "transparent", gap: 20 }}
      pointerEvents="box-none"
      // Prevent touch events from bubbling to document (which might cause focus loss)
      onTouchStart={(e) => {
        if (Platform.OS === 'web' && isTouchDeviceRef.current) {
          e.stopPropagation();
          shouldPreventBlurRef.current = true;
          // Reset after a short delay
          setTimeout(() => {
            shouldPreventBlurRef.current = false;
          }, 100);
        }
      }}
      onTouchEnd={(e) => {
        if (Platform.OS === 'web' && isTouchDeviceRef.current) {
          e.stopPropagation();
        }
      }}
      onTouchMove={(e) => {
        if (Platform.OS === 'web' && isTouchDeviceRef.current) {
          e.stopPropagation();
        }
      }}
    >
      {props.Progress && Platform.OS === 'web' &&
        <View style={{ flex: 1, maxHeight: 15, alignSelf: "center", flexDirection: 'row', maxWidth: '70%', justifyContent: 'center', gap: 20, marginVertical: 10 }}>
          <View style={{ minHeight: 10, maxWidth: '100%' }}>
            {LinearProgress && (
              <LinearProgress variant="determinate" value={handleUpdateProgressBar()} />
            )}
          </View>
        </View>
      }
      {Platform.OS === 'web' ? (
        <View style={{
          flex: 1,
          gap: 30,
          minWidth: props.staged ? 400 : undefined,
          backgroundColor: 'transparent',
          zIndex: 1
        }}>
          {
            props.scrollEnabled ?
              <ScrollView
                scrollEnabled={props.scrollEnabled || true}
                showsVerticalScrollIndicator={props.scrollBarVisible !== undefined ? props.scrollBarVisible : true}
                nestedScrollEnabled
                keyboardShouldPersistTaps="handled"
                keyboardDismissMode={Platform.OS === 'web' ? "none" : "on-drag"}
                contentContainerStyle={{
                  backgroundColor: 'transparent',
                  gap: 20,
                  paddingTop: 10,
                  alignItems: 'flex-start',
                  justifyContent: 'flex-start',
                  zIndex: 1
                }}
              >
                {props.staged ? (
                  <RenderStagedRows
                    otherVals={props.otherVals}
                    hideLabel={false}
                    blackText={undefined}
                    visibleFields={memoizedVisibleFields}
                    currentIndex={currentQuestionIndex}
                    nextFieldFunction={handleFocusNextField}
                    setInputRef={setInputRef}
                    values={values}
                    validateForm={validateForm}
                    readOnly={props.readOnly}
                  />
                ) : (
                    <RenderRows hideLabel={props.hideLabel} blackText={undefined} otherVals={props.otherVals} nextFieldFunction={handleFocusNextField} setInputRef={setInputRef} schemaArray={memoizedVisibleFields} columns={columns} formInstanceId={formInstanceId} readOnly={props.readOnly} inlineFields={props.inlineFields} />
                )}
              </ScrollView>
              :
              <View style={{
                minHeight: Platform.OS === 'web' ? 'auto' : 200,
                overflow: 'visible',
                position: Platform.OS === 'web' ? 'relative' : undefined,
                zIndex: Platform.OS === 'web' ? 1 : undefined
              }}>
                {props.staged ? (
                  <RenderStagedRows
                    otherVals={props.otherVals}
                    hideLabel={props.hideLabel}
                    blackText={undefined}
                    visibleFields={memoizedVisibleFields}
                    currentIndex={currentQuestionIndex}
                    nextFieldFunction={handleFocusNextField}
                    setInputRef={setInputRef}
                    values={values}
                    validateForm={validateForm}
                    readOnly={props.readOnly}
                  />
                ) : (
                    <RenderRows hideLabel={props.hideLabel} blackText={undefined} otherVals={props.otherVals} nextFieldFunction={handleFocusNextField} setInputRef={setInputRef} schemaArray={memoizedVisibleFields} columns={columns} formInstanceId={formInstanceId} readOnly={props.readOnly} inlineFields={props.inlineFields} />
                )}
              </View>
          }
          {(() => {
            const shouldShowButton = !props.internalButton && !props.removeSubmit && !props.hideButton;

            if (!shouldShowButton) {
              return null;
            }

            return (
              <View style={[
                {
                  width: '100%',
                  maxWidth: '100%',
                  alignItems: 'stretch',
                  justifyContent: 'center',
                  paddingHorizontal: 16,
                  paddingVertical: 16,
                  backgroundColor: 'transparent',
                  flexDirection: (props.showSkipButton || props.onCancel) ? 'row' : 'column',
                  gap: (props.showSkipButton || props.onCancel) ? 12 : 0,
                  flexWrap: 'nowrap',
                },
                props.buttonStyle,
                { backgroundColor: 'transparent' }
              ]}>
                {props.showSkipButton && (
                  <Pressable
                    onPress={() => {
                      console.log('[GenerateSchemaForm] Skip button pressed');
                      if (props.onSkip) {
                        props.onSkip();
                      }
                    }}
                    disabled={isSubmitting}
                    style={{
                      minHeight: 48,
                      minWidth: 120,
                      maxWidth: Platform.OS === 'web' ? 200 : '100%',
                      paddingVertical: 14,
                      paddingHorizontal: 24,
                      borderRadius: 12,
                      backgroundColor: 'transparent',
                      borderWidth: 1.5,
                      borderColor: theme ? '#6b7280' : '#9ca3af',
                      justifyContent: 'center',
                      alignItems: 'center',
                      opacity: isSubmitting ? 0.6 : 1,
                    }}
                  >
                    <Text style={{
                      color: theme ? '#6b7280' : '#9ca3af',
                      fontSize: 16,
                      fontWeight: '600',
                    }}>
                      {props.skipButtonLabel || 'Skip'}
                    </Text>
                  </Pressable>
                )}
                {props.onCancel && (
                  <Button
                    disabled={isSubmitting}
                    isLoading={false}
                    isSuccess={false}
                    icon={undefined}
                    onPress={() => {
                      console.log('[GenerateSchemaForm] Cancel button pressed');
                      if (props.onCancel) {
                        props.onCancel();
                      }
                    }}
                    buttonText={props.cancelButtonLabel || 'Cancel'}
                    customStyle={{
                      minHeight: 48,
                      minWidth: 0,
                      maxWidth: '100%',
                      width: '100%',
                      flex: 1,
                      flexShrink: 1,
                    }}
                    defaultColor="#6b7280"
                  />
                )}
                <Button disabled={props.removeSubmit}
                  icon={props.buttonIcon}
                  isSuccess={status?.success === true}
                  isLoading={isSubmitting}
                  customStyle={{
                    minHeight: 48,
                    minWidth: 0,
                    maxWidth: '100%',
                    width: (props.showSkipButton || props.onCancel) ? '100%' : (Platform.OS === 'web' ? 'auto' : '100%'),
                    flex: (props.showSkipButton || props.onCancel) ? 1 : undefined,
                    flexShrink: 1,
                    alignSelf: (props.showSkipButton || props.onCancel) ? undefined : 'center',
                  }}
                  defaultColor={props.buttonColor ? (Object.keys(errors)?.length != 0 ? '#9ca3af' : props.buttonColor) : '#3b82f6'}
                  onPress={() => {
                    setSubmitting(true);
                    validateForm().then(res => {
                      // CRITICAL: Filter out validation errors for hidden/conditional fields
                      const visibleFields = getVisibleFields(values || {});
                      const visibleFieldNames = new Set(visibleFields.map(f => f.title || f.key));
                      const filteredErrors: any = {};
                      if (res) {
                        Object.keys(res).forEach(fieldName => {
                          if (visibleFieldNames.has(fieldName)) {
                            // Only include errors for visible fields
                            filteredErrors[fieldName] = res[fieldName];
                          } else {
                            console.log(`⏭️  Skipping validation error for hidden field: ${fieldName}`);
                          }
                        });
                      }

                      if (filteredErrors && Object.keys(filteredErrors)?.length != 0) {
                        setFormikState(prevState => ({ ...prevState, touched: setNestedObjectValues<FormikTouched<FormValues>>(filteredErrors, true) }));
                        setTimeout(() => {
                          setFormikState(prevState => ({ ...prevState, status: null }));
                        }, 500);
                        setSubmitting(false);
                      } else {

                        // CRITICAL: Clear validation errors for hidden fields in Formik state
                        // This prevents Formik's handleSubmit from seeing errors for hidden fields
                        const visibleFields = getVisibleFields(values || {});
                        const visibleFieldNames = new Set(visibleFields.map(f => f.title || f.key));

                        // Create a cleaned errors object with only visible field errors
                        const cleanedErrors: any = {};
                        if (errors) {
                          Object.keys(errors).forEach(fieldName => {
                            if (visibleFieldNames.has(fieldName)) {
                              cleanedErrors[fieldName] = errors[fieldName];
                            }
                          });
                        }

                        console.log("🧹 [GenerateSchemaForm] Cleaning Formik errors - removing hidden field errors");
                        console.log("📊 Errors before cleaning:", Object.keys(errors || {}));
                        console.log("📊 Errors after cleaning:", Object.keys(cleanedErrors));

                        // Update Formik's errors state to exclude hidden field errors - only call setFormikState once
                        // This ensures handleSubmit() won't see errors for hidden fields
                        setFormikState((prevState: any) => ({
                          ...prevState,
                          errors: cleanedErrors
                        }));

                        setSubmitting(true);

                        // Now call handleSubmit - it will validate, but we've already cleared the errors
                        // So the validation should pass (or at least only show visible field errors)
                        try {
                          console.log("🚀 [GenerateSchemaForm] Triggering handleSubmit()...");
                          handleSubmit();
                          console.log("✅ [GenerateSchemaForm] handleSubmit() called");
                        } catch (error) {
                          console.error("❌ [GenerateSchemaForm] Error calling handleSubmit():", error);
                          setSubmitting(false);
                        }
                      }
                    }).catch(error => {
                      console.error("❌ [GenerateSchemaForm] Validation error:", error);
                      setSubmitting(false);
                    });
                  }} buttonText={props.buttonLabel || 'Submit'} />
              </View>
            );
          })()}
        </View>
      ) : (
        <KeyboardAvoidingView style={{
          flex: 1,
          gap: 30,
          backgroundColor: 'transparent',
          zIndex: 1
        }}
          keyboardVerticalOffset={Platform.OS === 'ios' ? 0 : 20}
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
          enabled={isDevice && !props.avoidKeyboardDisabled}>
          {
            props.scrollEnabled ?
              <ScrollView
                scrollEnabled={props.scrollEnabled || true}
                showsVerticalScrollIndicator={props.scrollBarVisible !== undefined ? props.scrollBarVisible : true}
                nestedScrollEnabled
                keyboardShouldPersistTaps="handled"
                keyboardDismissMode="on-drag"
                contentContainerStyle={{
                  backgroundColor: 'transparent',
                  gap: 20,
                  paddingTop: 10,
                  alignItems: 'flex-start',
                  justifyContent: 'flex-start',
                  zIndex: 1
                }}
              >
                {props.staged ? (
                  <RenderStagedRows
                    otherVals={props.otherVals}
                    hideLabel={false}
                    blackText={undefined}
                    visibleFields={memoizedVisibleFields}
                    currentIndex={currentQuestionIndex}
                    nextFieldFunction={handleFocusNextField}
                    setInputRef={setInputRef}
                    values={values}
                    validateForm={validateForm}
                    readOnly={props.readOnly}
                  />
                ) : (
                      <RenderRows hideLabel={props.hideLabel} blackText={undefined} otherVals={props.otherVals} nextFieldFunction={handleFocusNextField} setInputRef={setInputRef} schemaArray={memoizedVisibleFields} columns={columns} formInstanceId={formInstanceId} readOnly={props.readOnly} inlineFields={props.inlineFields} />
                )}
              </ScrollView>
              :
              <View style={{
                minHeight: 200,
                overflow: 'visible',
                zIndex: 1
              }}>
                {props.staged ? (
                  <RenderStagedRows
                    otherVals={props.otherVals}
                    hideLabel={props.hideLabel}
                    blackText={undefined}
                    visibleFields={memoizedVisibleFields}
                    currentIndex={currentQuestionIndex}
                    nextFieldFunction={handleFocusNextField}
                    setInputRef={setInputRef}
                    values={values}
                    validateForm={validateForm}
                    readOnly={props.readOnly}
                  />
                ) : (
                      <RenderRows hideLabel={props.hideLabel} blackText={undefined} otherVals={props.otherVals} nextFieldFunction={handleFocusNextField} setInputRef={setInputRef} schemaArray={memoizedVisibleFields} columns={columns} formInstanceId={formInstanceId} readOnly={props.readOnly} inlineFields={props.inlineFields} />
                )}
              </View>
          }
          {(() => {
            const shouldShowButton = !props.internalButton && !props.removeSubmit && !props.hideButton;

            if (!shouldShowButton) {
              return null;
            }

            return (
              <View style={[
                {
                  width: '100%',
                  maxWidth: '100%',
                  alignItems: 'stretch',
                  justifyContent: 'center',
                  paddingHorizontal: 16,
                  paddingVertical: 16,
                  backgroundColor: 'transparent',
                  flexDirection: (props.showSkipButton || props.onCancel) ? 'row' : 'column',
                  gap: (props.showSkipButton || props.onCancel) ? 12 : 0,
                  flexWrap: 'nowrap',
                },
                props.buttonStyle,
                { backgroundColor: 'transparent' }
              ]}>
                {props.showSkipButton && (
                  <Pressable
                    onPress={() => {
                      console.log('[GenerateSchemaForm] Skip button pressed');
                      if (props.onSkip) {
                        props.onSkip();
                      }
                    }}
                    disabled={isSubmitting}
                    style={{
                      minHeight: 48,
                      minWidth: 120,
                      maxWidth: '100%',
                      paddingVertical: 14,
                      paddingHorizontal: 24,
                      borderRadius: 12,
                      backgroundColor: 'transparent',
                      borderWidth: 1.5,
                      borderColor: theme ? '#6b7280' : '#9ca3af',
                      justifyContent: 'center',
                      alignItems: 'center',
                      opacity: isSubmitting ? 0.6 : 1,
                    }}
                  >
                    <Text style={{
                      color: theme ? '#6b7280' : '#9ca3af',
                      fontSize: 16,
                      fontWeight: '600',
                    }}>
                      {props.skipButtonLabel || 'Skip'}
                    </Text>
                  </Pressable>
                )}
                {props.onCancel && (
                  <Button
                    disabled={isSubmitting}
                    isLoading={false}
                    isSuccess={false}
                    icon={undefined}
                    onPress={() => {
                      console.log('[GenerateSchemaForm] Cancel button pressed');
                      if (props.onCancel) {
                        props.onCancel();
                      }
                    }}
                    buttonText={props.cancelButtonLabel || 'Cancel'}
                    customStyle={{
                      minHeight: 48,
                      minWidth: 0,
                      maxWidth: '100%',
                      width: '100%',
                      flex: 1,
                      flexShrink: 1,
                    }}
                    defaultColor="#6b7280"
                  />
                )}
                <Button disabled={props.removeSubmit}
                  icon={props.buttonIcon}
                  isSuccess={status?.success === true}
                  isLoading={isSubmitting}
                  customStyle={{
                    minHeight: 48,
                    minWidth: 0,
                    maxWidth: '100%',
                    width: (props.showSkipButton || props.onCancel) ? '100%' : '100%',
                    flex: (props.showSkipButton || props.onCancel) ? 1 : undefined,
                    flexShrink: 1,
                    alignSelf: (props.showSkipButton || props.onCancel) ? undefined : 'center',
                  }}
                  defaultColor={props.buttonColor ? (Object.keys(errors)?.length != 0 ? '#9ca3af' : props.buttonColor) : '#3b82f6'}
                  onPress={() => {
                    setSubmitting(true);
                    validateForm().then(res => {
                      // CRITICAL: Filter out validation errors for hidden/conditional fields
                      const visibleFields = getVisibleFields(values || {});
                      const visibleFieldNames = new Set(visibleFields.map(f => f.title || f.key));
                      const filteredErrors: any = {};
                      if (res) {
                        Object.keys(res).forEach(fieldName => {
                          if (visibleFieldNames.has(fieldName)) {
                            // Only include errors for visible fields
                            filteredErrors[fieldName] = res[fieldName];
                          } else {
                            console.log(`⏭️  Skipping validation error for hidden field: ${fieldName}`);
                          }
                        });
                      }

                      if (filteredErrors && Object.keys(filteredErrors)?.length != 0) {
                        setFormikState(prevState => ({ ...prevState, touched: setNestedObjectValues<FormikTouched<FormValues>>(filteredErrors, true) }));
                        setTimeout(() => {
                          setFormikState(prevState => ({ ...prevState, status: null }));
                        }, 500);
                        setSubmitting(false);
                      } else {

                        // CRITICAL: Clear validation errors for hidden fields in Formik state
                        // This prevents Formik's handleSubmit from seeing errors for hidden fields
                        const visibleFields = getVisibleFields(values || {});
                        const visibleFieldNames = new Set(visibleFields.map(f => f.title || f.key));

                        // Create a cleaned errors object with only visible field errors
                        const cleanedErrors: any = {};
                        if (errors) {
                          Object.keys(errors).forEach(fieldName => {
                            if (visibleFieldNames.has(fieldName)) {
                              cleanedErrors[fieldName] = errors[fieldName];
                            }
                          });
                        }

                        console.log("🧹 [GenerateSchemaForm] Cleaning Formik errors - removing hidden field errors");
                        console.log("📊 Errors before cleaning:", Object.keys(errors || {}));
                        console.log("📊 Errors after cleaning:", Object.keys(cleanedErrors));

                        // Update Formik's errors state to exclude hidden field errors - only call setFormikState once
                        // This ensures handleSubmit() won't see errors for hidden fields
                        setFormikState((prevState: any) => ({
                          ...prevState,
                          errors: cleanedErrors
                        }));

                        setSubmitting(true);

                        // Now call handleSubmit - it will validate, but we've already cleared the errors
                        // So the validation should pass (or at least only show visible field errors)
                        try {
                          console.log("🚀 [GenerateSchemaForm] Triggering handleSubmit()...");
                          handleSubmit();
                          console.log("✅ [GenerateSchemaForm] handleSubmit() called");
                        } catch (error) {
                          console.error("❌ [GenerateSchemaForm] Error calling handleSubmit():", error);
                          setSubmitting(false);
                        }
                      }
                    }).catch(error => {
                      console.error("❌ [GenerateSchemaForm] Validation error:", error);
                      setSubmitting(false);
                    });
                  }} buttonText={props.buttonLabel || 'Submit'} />
              </View>
            );
          })()}
        </KeyboardAvoidingView>
      )}
    </View>
  );
}, (prevProps, nextProps) => {
  // Custom comparison function to prevent unnecessary re-renders
  // Only re-render if significant props change, not on every Formik state update (like values/touched)

  // Always re-render if these change (they affect structure)
  if (
    prevProps.props.schema !== nextProps.props.schema ||
    prevProps.props.staged !== nextProps.props.staged ||
    prevProps.schemaArray.length !== nextProps.schemaArray.length ||
    prevProps.formInstanceId !== nextProps.formInstanceId ||
    prevProps.currentQuestionIndex !== nextProps.currentQuestionIndex ||
    prevProps.isDevice !== nextProps.isDevice ||
    prevProps.columns !== nextProps.columns
  ) {
    return false; // Props changed, allow re-render
  }

  // Check formikProps that matter for rendering
  // Don't compare values/touched as they change on every keystroke
  const prevErrors = prevProps.formikProps.errors || {};
  const nextErrors = nextProps.formikProps.errors || {};
  const errorsChanged = JSON.stringify(Object.keys(prevErrors).sort()) !==
    JSON.stringify(Object.keys(nextErrors).sort());

  if (
    prevProps.formikProps.isSubmitting !== nextProps.formikProps.isSubmitting ||
    prevProps.formikProps.status !== nextProps.formikProps.status ||
    errorsChanged
  ) {
    return false; // Significant formik state changed, allow re-render
  }

  // No significant changes, prevent re-render
  return true;
});

const GenerateSchemaForm = forwardRef<FormikProps<any>, SchemaObject>((props, ref) => {
  const [schemaArray, setSchemaArray] = useState([]);
  const [loading, setLoading] = useState(true);
  const [initialValues, setInitialValues] = useState(props.initialData || {});
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const id = useGlobalUser();
  const combinedIds = useMemo(() => ({ globalId: id, dataId: props.other, extraId: props.extraId || null }), [id, props.other, props.extraId]);
  const inputRefs = useRef({});
  const isDevice = Platform.OS !== 'web';
  const [columns, setColumns] = useState(1);
  const endReachedTriggered = useRef(false); // Track if endReached has been triggered for current question
  // Generate a unique form instance ID to prevent duplicate input IDs
  const formInstanceId = useRef(`form-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`).current;
  // Detect touch device for web to prevent keyboard dismissal
  const isTouchDeviceRef = useRef<boolean>(false);
  // Track if we should prevent blur after touch events
  const shouldPreventBlurRef = useRef<boolean>(false);
  // Track focus time to prevent immediate blur after focus
  const focusTimeRef = useRef<number>(0);
  // Track if schema has been processed to prevent re-processing on re-renders
  const schemaProcessedRef = useRef<boolean>(false);
  const processedSchemaRef = useRef<string | null>(null);
  // Track window dimensions for keyboard detection
  const windowDimensionsTrackingRef = useRef<{ width: number; height: number } | null>(null);

  useEffect(() => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      isTouchDeviceRef.current = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

      // Initialize dimensions
      windowDimensionsTrackingRef.current = {
        width: window.innerWidth,
        height: window.innerHeight,
      };

      // REMOVED: Window resize listener - it was causing parent LoginScreen to re-render
      // The resize event bubbles up and triggers useWindowDimensions() in LoginScreen
      // Even though we weren't setting state, the window resize itself causes the hook to update
      // This was causing LoginScreen to re-render when keyboard appeared in the form

      // REMOVED: Visual viewport listener - it was causing re-renders on keyboard appearance
      // The viewport changes were triggering component re-renders even though we weren't setting state
      // This was interrupting form focus on touch devices
    }
  }, []);
  // const [reset, setReset] = useState(false);
  // const [submitDisabled, setSubmitDisabled] = useState(false);

  const propChangeCount = useRef(0);

  // Function to evaluate conditions for field visibility - supports both backend and frontend formats
  // NOTE: No dependencies - this function is pure and doesn't depend on schemaArray
  const evaluateCondition = useCallback((fieldSchema: any, formValues: any, allSchemaFields: any[] = []): boolean => {
    const fieldName = fieldSchema.title || fieldSchema.key;

    
    // Backend format: check for dependsOn or validation.dependsOn
    if (fieldSchema.dependsOn || fieldSchema.validation?.dependsOn) {
      const dependsOnConfig = fieldSchema.dependsOn || fieldSchema.validation?.dependsOn;
      
      const { field: dependentFieldName, value: expectedValue } = dependsOnConfig;
      
      // First try to find the value using the exact field name (check both title and key)
      let actualValue = formValues[dependentFieldName];
      
      // If not found, try to find the field in schema and use its key
      if (actualValue === undefined && allSchemaFields.length > 0) {
        const dependentField = allSchemaFields.find(f => 
          (f.title || f.key) === dependentFieldName || 
          f.title === dependentFieldName || 
          f.key === dependentFieldName
        );
        if (dependentField) {
          // Try both title and key as form value keys
          actualValue = formValues[dependentField.key] ?? formValues[dependentField.title] ?? formValues[dependentFieldName];
        }
      }
      
      // Only try field mapping if the exact field name doesn't exist in the form values
      // AND the field doesn't exist in the schema
      const fieldExistsInSchema = allSchemaFields.some(field => 
        (field.title || field.key) === dependentFieldName || 
        field.title === dependentFieldName || 
        field.key === dependentFieldName
      );
      
      if (actualValue === undefined && !fieldExistsInSchema && allSchemaFields.length > 0) {
        // Create a mapping for common field name patterns
        const fieldMappings = {
          'is_business': ['business', 'organisation', 'organization', 'company'],
          'business': ['business', 'organisation', 'organization', 'company'],
          'industry': ['industry'],
          'company_size': ['company', 'size', 'employees'],
          'decision_maker': ['decision', 'maker'],
          'budget_range': ['budget', 'range']
        };
        
        // Look for a field that matches the dependent field name
        const matchingField = allSchemaFields.find(field => {
          const fieldKey = (field.title || field.key || '').toLowerCase();
          const dependentLower = dependentFieldName.toLowerCase();
          
          // Check if it's a known mapping
          const mappingKeys = fieldMappings[dependentLower] || [dependentLower];
          return mappingKeys.some(key => fieldKey.includes(key));
        });
        
        if (matchingField) {
          actualValue = formValues[matchingField.title || matchingField.key];
        }
      }

      let result;
      
      // Handle initial load case - if the dependent field hasn't been set yet
      if (actualValue === undefined || actualValue === null) {
        // On initial load or when dependent field is unset
        const dependentField = allSchemaFields.find(f => (f.title || f.key) === dependentFieldName);
        if (dependentField?.type === 'boolean') {
          // For boolean dependencies, assume initial value is false
          result = expectedValue === 'false' || expectedValue === false;
          console.log(`🔄 Initial boolean handling for ${fieldName}: assuming ${dependentFieldName} = false, expected: ${expectedValue}, result: ${result}`);
        } else {
          // For other field types, hide conditional fields initially
          result = false;
          console.log(`🔄 Initial handling for ${fieldName}: ${dependentFieldName} is undefined, hiding field`);
        }
      } else {
        // Normal evaluation when form has values - handle both string and boolean comparisons
        if (Array.isArray(expectedValue)) {
          result = expectedValue.includes(actualValue);
        } else {
          // Handle boolean comparisons more flexibly
          if (typeof expectedValue === 'string' && (expectedValue === 'true' || expectedValue === 'false')) {
            // Convert actualValue to string for comparison with string boolean values
            const actualStr = String(actualValue).toLowerCase();
            result = actualStr === expectedValue;
          } else if (typeof expectedValue === 'boolean') {
            // Convert actualValue to boolean for comparison with boolean values
            const actualBool = actualValue === true || actualValue === 'true';
            result = actualBool === expectedValue;
          } else {
            // Normal string/number comparison
            result = actualValue === expectedValue;
          }
        }
      }
      
      console.log(`${result ? '✅' : '❌'} DependsOn result for ${fieldName}:`, {
        dependentFieldName,
        actualValue,
        actualValueType: typeof actualValue,
        expectedValue,
        expectedValueType: typeof expectedValue,
        result,
        comparison: `${actualValue} (${typeof actualValue}) === ${expectedValue} (${typeof expectedValue})`
      });
      
      return result;
    }

    // Frontend format: check for conditional with operator
    if (fieldSchema.conditional) {
      const { field, operator, value } = fieldSchema.conditional;
      // Try to find the field value - check both direct key and schema field key/title
      let fieldValue = formValues[field];
      
      // If not found, try to find the field in schema and use its key
      if (fieldValue === undefined && allSchemaFields.length > 0) {
        const conditionalField = allSchemaFields.find(f => 
          (f.title || f.key) === field || 
          f.title === field || 
          f.key === field
        );
        if (conditionalField) {
          // Try both title and key as form value keys
          fieldValue = formValues[conditionalField.key] ?? formValues[conditionalField.title] ?? formValues[field];
        }
      }

      let conditionalResult;
      switch (operator) {
        case 'equals':
          conditionalResult = fieldValue === value;
          break;
        case 'not_equals':
          conditionalResult = fieldValue !== value;
          break;
        case 'contains':
          conditionalResult = Array.isArray(fieldValue) ? fieldValue.includes(value) : String(fieldValue).includes(value);
          break;
        case 'not_contains':
          conditionalResult = Array.isArray(fieldValue) ? !fieldValue.includes(value) : !String(fieldValue).includes(value);
          break;
        case 'greater_than':
          conditionalResult = Number(fieldValue) > Number(value);
          break;
        case 'less_than':
          conditionalResult = Number(fieldValue) < Number(value);
          break;
        case 'is_empty':
          conditionalResult = !fieldValue || fieldValue === '' || (Array.isArray(fieldValue) && fieldValue.length === 0);
          break;
        case 'is_not_empty':
          conditionalResult = fieldValue && fieldValue !== '' && (!Array.isArray(fieldValue) || fieldValue.length > 0);
          break;
        default:
          conditionalResult = true;
          break;
      }

      
      return conditionalResult;
    }

    // No conditions found, field is visible
    return true;
  }, []); // No dependencies - function is pure, allSchemaFields is passed as parameter

  // Get visible fields based on current values and conditions
  const getVisibleFields = useCallback((formValues: any) => {
    const results = schemaArray.map(field => {
      const isVisible = evaluateCondition(field, formValues, schemaArray);
      return {
        field: field.title || field.key,
        isVisible,
        hasCondition: !!(field.conditional || field.dependsOn || field.validation?.dependsOn)
      };
    });
    
    
    return schemaArray.filter(field => evaluateCondition(field, formValues, schemaArray));
  }, [schemaArray, evaluateCondition]);

  // Navigation functions for staged form
  const goToNextQuestion = useCallback((visibleFields: any[], formValues: any, validateForm: Function) => {
    if (currentQuestionIndex < visibleFields.length - 1) {
      const currentField = visibleFields[currentQuestionIndex];

      // Validate current field before proceeding
      if (currentField?.validation && currentField.validation.required) {
        const fieldValue = formValues[currentField.key];
        if (!fieldValue || (Array.isArray(fieldValue) && fieldValue.length === 0)) {
          return false; // Validation failed, don't proceed
        }
      }

      setCurrentQuestionIndex(prev => {
        const nextIndex = prev + 1;
        // Reset the endReached flag when moving to a new question
        endReachedTriggered.current = false;

        // Check if we're reaching the last question
        if (nextIndex === visibleFields.length - 1 && props.endReached) {
          // Trigger endReached callback when reaching the last question
          setTimeout(() => {
            if (!endReachedTriggered.current) {
              endReachedTriggered.current = true;
              props.endReached?.(formValues);
            }
          }, 100);
        }
        return nextIndex;
      });
      return true;
    }
    return false;
  }, [currentQuestionIndex, props.endReached]);

  const goToPreviousQuestion = useCallback(() => {
    if (currentQuestionIndex > 0) {
      // Reset the endReached flag when moving to a different question
      endReachedTriggered.current = false;
      setCurrentQuestionIndex(prev => prev - 1);
    }
  }, [currentQuestionIndex]);

  // Effect to trigger endReached callback when reaching the last question
  useEffect(() => {
    if (props.staged && props.endReached && schemaArray.length > 0) {
      // This effect will be triggered by currentQuestionIndex changes
      // The actual callback with values will be handled in the Formik render function
    }
  }, [currentQuestionIndex, schemaArray.length, props.staged, props.endReached]);

  // Track changes to props
  useEffect(() => {
    propChangeCount.current += 1;
  }, [props]);

  // useEffect(() => {
  //   if (props.initialData) {
  //     setInitialValues(props.initialData)
  //   }
  // }, [props.initialData, props.schema])

  // useLayoutEffect(() => {
  //   if (props.containerWidth && props.columns > 1)
  //     calculateColumns(props.containerWidth);
  // }, [])

  // const calculateColumns = (containerWidth = 420) => {
  //   if(props.columns && containerWidth > 350){
  //     //Try to force columns unless container width is less than 350
  //     setColumns(props.columns);
  //     return;
  //   }
  //   const maxColumnWidth = 420; // Maximum width for each column
  //   if (Platform.OS === 'web') {
  //     setColumns(Math.floor(containerWidth / maxColumnWidth));
  //   } else {
  //     setColumns(1);
  //   }
  // };

  const RenderRows = React.memo<{
    otherVals: any;
    hideLabel?: boolean;
    blackText?: any;
    schemaArray: any[];
    nextFieldFunction?: any;
    setInputRef?: any;
    columns?: number;
    formInstanceId?: string;
    readOnly?: boolean;
    inlineFields?: boolean;
  }>(({ otherVals, hideLabel, blackText, schemaArray, nextFieldFunction, setInputRef, columns = 1, formInstanceId, readOnly = false, inlineFields = false }) => {
    // Track renders for debugging
    const renderCountRef = useRef(0);
    const prevSchemaArrayRef = useRef(schemaArray);
    renderCountRef.current += 1;

    useEffect(() => {
      // Only log if schema array actually changed (not just reference)
      const schemaChanged = prevSchemaArrayRef.current.length !== schemaArray.length ||
        prevSchemaArrayRef.current.some((item, idx) => {
          const newItem = schemaArray[idx];
          return !newItem || item.key !== newItem.key || item.title !== newItem.title;
        });

      if (schemaChanged || renderCountRef.current === 1) {
        if (__DEV__) {
          console.log(`🔄 [RenderRows] Render #${renderCountRef.current}`, {
            schemaArrayLength: schemaArray.length,
            columns,
            formInstanceId,
            timestamp: new Date().toISOString(),
          });
        }
        prevSchemaArrayRef.current = schemaArray;
      }
    }, [schemaArray, columns, formInstanceId]);
    // Add htmlId to schema items for unique DOM IDs, but keep original key for Formik field names
    const uniqueSchemaArray = useMemo(() => {
      return schemaArray.map(item => ({
        ...item,
        // Keep original key for Formik field name
        key: item.key || item.title,
        // Add separate htmlId for DOM element uniqueness
        htmlId: formInstanceId ? `${formInstanceId}-${item.key || item.title}` : (item.key || item.title)
      }));
    }, [schemaArray, formInstanceId]);

    // If inlineFields is true, render all fields in XStack with flexWrap="wrap"
    if (inlineFields) {
      return useMemo(() => {
        return (
          <XStack flexWrap="wrap" gap={20} padding={5}>
            {uniqueSchemaArray.map((item, index) => {
              const fieldName = item.key || item.title;
              const htmlId = item.htmlId || fieldName;
              // Check if field is object/group type - these should render full width
              const fieldType = item.ref || item.validation?.type || item.type || item.content?.type;
              const isObjectOrGroup = fieldType === 'object' || fieldType === 'group' || fieldType === 'Object';

              return (
                <View
                  key={htmlId}
                  style={isObjectOrGroup
                    ? { minWidth: '100%', width: '100%' }
                    : { maxWidth: 400, flex: 1, minWidth: 300 }
                  }
                >
                  <Row
                    otherVals={otherVals}
                    column
                    hideLabel={hideLabel}
                    blackText={blackText}
                    allItems={uniqueSchemaArray}
                    items={[item]}
                    index={index}
                    totalRowsCount={uniqueSchemaArray.length}
                    nextFieldFunction={nextFieldFunction}
                    setInputRef={setInputRef}
                    readOnly={readOnly}
                    inlineFields={inlineFields}
                  />
                </View>
              );
            })}
          </XStack>
        );
      }, [uniqueSchemaArray, blackText, otherVals, hideLabel, nextFieldFunction, setInputRef, readOnly, inlineFields]);
    }

    // Calculate actual number of rows
    const totalRows = Math.ceil(uniqueSchemaArray.length / columns);

    if (columns <= 1) {
      return useMemo(() => {
        return (
          <Row
            otherVals={otherVals}
            column
            hideLabel={hideLabel}
            blackText={blackText}
            allItems={uniqueSchemaArray}
            items={uniqueSchemaArray}
            index={0}
            totalRowsCount={totalRows}
            nextFieldFunction={nextFieldFunction}
            setInputRef={setInputRef}
            readOnly={readOnly}
            inlineFields={inlineFields}
          />
        );
      }, [uniqueSchemaArray, blackText, otherVals, hideLabel, nextFieldFunction, setInputRef, totalRows]);
    }

    const rows = [];
    for (let i = 0; i < uniqueSchemaArray?.length; i += columns) {
      // Calculate actual row number (0-based)
      const rowNumber = Math.floor(i / columns);
      rows.push(
        <Row
          otherVals={otherVals}
          column={false}
          key={i}
          hideLabel={hideLabel}
          blackText={blackText}
          allItems={uniqueSchemaArray}
          items={uniqueSchemaArray.slice(i, i + columns)}
          index={rowNumber}
          totalRowsCount={totalRows}
          nextFieldFunction={undefined}
          setInputRef={undefined}
          readOnly={readOnly}
          inlineFields={inlineFields}
        />
      );
    }
    return rows;
  }, (prevProps, nextProps) => {
    // Custom comparison to prevent re-renders when only function references change
    return (
      prevProps.schemaArray.length === nextProps.schemaArray.length &&
      prevProps.schemaArray.every((item, idx) => {
        const nextItem = nextProps.schemaArray[idx];
        return nextItem && item.key === nextItem.key && item.title === nextItem.title;
      }) &&
      prevProps.columns === nextProps.columns &&
      prevProps.formInstanceId === nextProps.formInstanceId &&
      prevProps.hideLabel === nextProps.hideLabel &&
      prevProps.readOnly === nextProps.readOnly &&
      prevProps.blackText === nextProps.blackText &&
      prevProps.inlineFields === nextProps.inlineFields
    );
  });

  // Staged Row Renderer - Optimized to prevent input focus loss
  const RenderStagedRows = React.memo(({
    otherVals,
    hideLabel,
    blackText,
    visibleFields,
    currentIndex,
    nextFieldFunction,
    setInputRef,
    values,
    validateForm,
    readOnly
  }: {
    otherVals: any;
    hideLabel?: boolean;
    blackText?: boolean;
    visibleFields: any[];
    currentIndex: number;
    nextFieldFunction?: any;
    setInputRef?: any;
    values?: any;
    validateForm?: any;
    readOnly?: boolean;
  }) => {
    const theme = themeSelector();
    // Memoize visible fields properly - use stable reference to prevent re-renders on every keystroke
    // Only recalculate when field structure changes, not on every value change
    const memoizedVisibleFields = useMemo(() => visibleFields, [
      visibleFields?.length,
      visibleFields?.map(f => f.title || f.key).join(',')
      // Removed JSON.stringify(values) to prevent re-renders on every keystroke
    ]);

    if (!memoizedVisibleFields || memoizedVisibleFields.length === 0 || currentIndex >= memoizedVisibleFields.length) {
      return null;
    }

    const currentField = memoizedVisibleFields[currentIndex];
    const currentFieldArray = useMemo(() => [currentField], [currentField?.title || currentField?.key]);

    // Create a stable key that doesn't change during typing - use field title/key instead of index
    const stableKey = useMemo(() => `staged-${currentField?.title || currentField?.key || 'field'}`, [currentField?.title, currentField?.key]);

    // Memoize the Row component with stable dependencies
    const MemoizedRow = useMemo(() => (
      <Row
        key={stableKey}
        otherVals={otherVals}
        column={true}
        hideLabel={props.hideLabel || false}
        blackText={blackText}
        allItems={memoizedVisibleFields}
        items={currentFieldArray}
        index={currentIndex}
        totalRowsCount={memoizedVisibleFields.length}
        nextFieldFunction={nextFieldFunction}
        setInputRef={setInputRef}
        readOnly={readOnly}
      />
    ), [stableKey, otherVals, hideLabel, blackText, memoizedVisibleFields, currentFieldArray, currentIndex, nextFieldFunction, setInputRef, readOnly]);

    return (
      <View key={stableKey} style={{ minWidth: 600, width: '100%' }}>
        {/* Question Counter */}
        <View style={{
          alignItems: 'center',
          marginBottom: 20,
          paddingVertical: 10,
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          borderRadius: 12,
          marginHorizontal: 16
        }}>
          <Text style={{
            fontSize: 16,
            fontWeight: '600',
            color: theme? '#3b82f6' :"white",
          }}>
            Question {currentIndex + 1} of {memoizedVisibleFields.length}
          </Text>
        </View>

        {/* Progress Indicator Dots */}
        <View style={{ alignItems: 'center', marginBottom: 20 }}>
          <View style={{
            flexDirection: 'row',
            alignItems: 'center',
            gap: 4
          }}>
            {memoizedVisibleFields.map((_, index) => (
              <View
                key={index}
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: index === currentIndex ? '#3b82f6' :
                    index < currentIndex ? '#10b981' : '#e5e7eb'
                }}
              />
            ))}
          </View>
        </View>

        {/* Question Card */}
        <View style={{
          backgroundColor: theme ? 'rgba(255, 255, 255, 0.95)' : 'rgba(30, 41, 59, 0.95)',
          borderRadius: 16,
          marginHorizontal: 16,
          marginBottom: 20,
          padding: 20,
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 2 },
          shadowOpacity: 0.1,
          shadowRadius: 8,
          elevation: 3
        }}>
          {/* Question Title */}
          {currentField?.label && (
            <Text style={{
              fontSize: 18,
              fontWeight: '600',
              color: '#1a1a1a',
              marginBottom: 8,
              textAlign: 'center'
            }}>
              {currentField.label}
            </Text>
          )}

          {/* Question Description */}
          {currentField?.description && (
            <Text style={{
              fontSize: 14,
              color: '#666',
              textAlign: 'center',
              marginBottom: 16,
              lineHeight: 20
            }}>
              {currentField.description}
            </Text>
          )}

          {/* Input Field - Using memoized Row to prevent re-mounting */}
          {MemoizedRow}
        </View>

        {/* Navigation Controls */}
        <View style={{
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          paddingHorizontal: 20,
          paddingVertical: 16,
          backgroundColor: theme ? 'rgba(255, 255, 255, 0.95)' : 'rgba(30, 41, 59, 0.95)',
          borderRadius: 16,
          marginHorizontal: 16,
          marginBottom: 10,
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 2 },
          shadowOpacity: 0.1,
          zIndex:-1,
          shadowRadius: 8,
          elevation: 3
        }}>
          {/* Previous Button */}
          <View style={{ flex: 1, alignItems: 'flex-start' }}>
            {currentIndex > 0 && (
              <TouchableOpacity
                onPress={() => goToPreviousQuestion()}
                activeOpacity={0.7}
                style={{
                  backgroundColor: '#6b7280',
                  paddingHorizontal: 16,
                  paddingVertical: 10,
                  borderRadius: 8,
                  flexDirection: 'row',
                  alignItems: 'center'
                }}
              >
                <Text style={{
                  color: 'white',
                  fontSize: 14,
                  fontWeight: '600'
                }}>
                  ← Previous
                </Text>
              </TouchableOpacity>
            )}
          </View>

          {/* Spacer for center alignment */}
          <View style={{ flex: 2, alignItems: 'center' }}>
            <View />
          </View>

          {/* Next Button */}
          <View style={{ flex: 1, alignItems: 'flex-end' }}>
            {currentIndex < memoizedVisibleFields.length - 1 && (
              <TouchableOpacity
                onPress={() => goToNextQuestion(memoizedVisibleFields, values, validateForm)}
                activeOpacity={0.7}
                style={{
                  backgroundColor: '#3b82f6',
                  paddingHorizontal: 16,
                  paddingVertical: 10,
                  borderRadius: 8,
                  flexDirection: 'row',
                  alignItems: 'center'
                }}
              >
                <Text style={{
                  color: 'white',
                  fontSize: 14,
                  fontWeight: '600'
                }}>
                  Next →
                </Text>
              </TouchableOpacity>
            )}
          </View>
        </View>
      </View>
    );
  });

  useEffect(() => {
    const processSchema = () => {
      return new Promise((resolve, reject) => {
//Rearrange schema array if it contains images or image to bring that to the top: 
        let moddedSchema = [];

        if(!props.schema) {
          console.log('⚠️  No schema provided');
          resolve(initialValues);
          return;
        }

        // CRITICAL: Check if we've already processed this exact schema
        // Use schema reference comparison to prevent re-processing on re-renders
        const schemaKey = JSON.stringify(props.schema);
        if (schemaProcessedRef.current && processedSchemaRef.current === schemaKey) {
          console.log('⚠️  Schema already processed - skipping re-initialization');
          resolve(initialValues);
          return;
        }

        //Add check to see if schemaArray already exists
        if (schemaArray.length > 0 && schemaProcessedRef.current) {
          console.log('⚠️  Schema array already populated - skipping re-initialization');
          resolve(initialValues);
          return;
        }

        let initVals: any = {};
        let newSchemaArray: any = [];

        Object?.keys(props.schema)?.forEach((key) => {
          if (!key) return;
          try {
            const value = props.schema[key];
            if (!value) {
              return;
            }
            const newItem = value;

            if (newItem.title) {
              if (props?.nullify && props?.nullify?.includes(newItem?.title)) {
                return;
              } else {
                //If item does not exist in the new schema array, add it
                if (!newSchemaArray.find((item) => item.title === newItem?.title)) {
                  const fieldKey = newItem.key || newItem.title || key;
                  newSchemaArray.push({
                    ...newItem,
                    // Use provided key first, then fall back to title or array key
                    key: fieldKey
                  });
                }
                // Use the unique key for initial values instead of title
                const fieldKey = newItem.key || newItem.title;
                // Use if/else if/else chain to prevent overwriting
                if (newItem?.type === 'number') {
                  initVals[fieldKey] = 0;
                } else if (newItem?.type === 'date') {
                  initVals[fieldKey] = new Date();
                } else if (newItem?.type === 'dateTime') {
                  initVals[fieldKey] = new Date();
                } else if (newItem?.type === 'dropDown') {
                  initVals[fieldKey] = '';
                } else if (newItem?.type === 'image') {
                  initVals[fieldKey] = '';
                } else if (newItem?.type === 'array') {
                  initVals[fieldKey] = [];
                } else if (newItem?.type === 'object') {
                  initVals[fieldKey] = {};
                } else if (newItem?.type === 'boolean') {
                  initVals[fieldKey] = false;
                } else {
                  initVals[fieldKey] = '';
                }
              }
            } else {
              //Check if key exists in nullify array
              if (props?.nullify && props?.nullify?.includes(key)) {
                return;
              } else {
                //If item does not exist in the new schema array, add it
                const fieldKey = newItem.key || key;
                if (!newSchemaArray.find(item => item.key === fieldKey)) {
                  newSchemaArray.push({ 
                    title: key, 
                    key: fieldKey,
                    type: newItem.type || newItem.ref || "string", 
                    ref: newItem.ref || undefined, 
                    validation: newItem.validation, 
                    content: newItem.content,
                    conditional: newItem.conditional,
                    dependsOn: newItem.dependsOn,
                    placeholder: newItem.placeholder,
                    label: newItem.label,
                    description: newItem.description
                  });
                }
                // Use the unique key for initial values
                if (newItem?.type === 'number') {
                  initVals[fieldKey] = 0;
                } else if (newItem?.type === 'date') {
                  initVals[fieldKey] = new Date();
                } else if (newItem?.type === 'dateTime') {
                  initVals[fieldKey] = new Date();
                } else if (newItem?.type === 'dropDown') {
                  initVals[fieldKey] = '';
                } else if (newItem?.type === 'image') {
                  initVals[fieldKey] = '';
                } else if (newItem?.type === 'array') {
                  initVals[fieldKey] = [];
                } else if (newItem?.type === 'object') {
                  initVals[fieldKey] = {};
                } else if (newItem?.type === 'boolean') {
                  initVals[fieldKey] = false;
                } else if (newItem?.type === 'tags') {
                  initVals[fieldKey] = [];
                } else {
                  initVals[fieldKey] = '';
                }
              }

            }
          } catch (error) {
          }
        });


        // Set schema array first
        setSchemaArray(newSchemaArray);

        // Merge with initialData if provided and retainInitialValues is true
        const finalInitialValues = props.retainInitialValues && props.initialData
          ? { ...initVals, ...props.initialData }
          : props.initialData || initVals;

        // Set initial values after schema array
        setInitialValues(finalInitialValues);

        // Mark schema as processed to prevent re-initialization on re-renders
        schemaProcessedRef.current = true;
        processedSchemaRef.current = JSON.stringify(props.schema);

        resolve(finalInitialValues);
      });
    };

    const initializeSchema = async () => {
      setLoading(true);
      try {
        await processSchema();
      }
      catch (error) {
        console.error('❌ Error initializing schema:', error);
      } finally {
        setTimeout(() => {
          setLoading(false);
        }, 1000);
      }
    };

    // Only initialize if schema hasn't been processed yet
    if (!schemaProcessedRef.current || processedSchemaRef.current !== JSON.stringify(props.schema)) {
      initializeSchema();
    }
  }, [props.schema]);

  // Generate Yup validation schema - memoized to prevent unnecessary regeneration
  // NOTE: This schema includes ALL fields, but we filter validation errors for hidden fields in onSubmit
  const validationSchema = useMemo(() => {
    const schema = generateYupValidation(schemaArray);
    return schema;
  }, [schemaArray]);
  
  // Create a conditional validation function that only validates visible fields
  // Formik will use this instead of validationSchema if both are provided
  // NOTE: Validation is submit-only for web touch devices (validateOnBlur=false, validateOnChange=false)
  const conditionalValidate = useCallback(async (values: any) => {
    const visibleFields = getVisibleFields(values || {});
    const visibleFieldNames = new Set(visibleFields.map(f => f.title || f.key));
    
    // Get all validation errors using the schema
    let allErrors: any = {};
    try {
      await validationSchema.validate(values, { abortEarly: false });
      // If validation passes, return empty errors
      allErrors = {};
    } catch (err: any) {
      // Extract errors from validation exception
      if (err.inner) {
        err.inner.forEach((error: any) => {
          if (error.path) {
            allErrors[error.path] = error.message;
          }
        });
      }
    }
    
    // Filter to only visible fields
    const visibleErrors: any = {};
    Object.keys(allErrors || {}).forEach(fieldName => {
      if (visibleFieldNames.has(fieldName)) {
        visibleErrors[fieldName] = allErrors[fieldName];
      }
    });
    
    // Return empty object if no errors, or errors object if there are errors
    return Object.keys(visibleErrors).length > 0 ? visibleErrors : undefined;
  }, [validationSchema, getVisibleFields]);

  // Display form errors
  const displayErrors = (errors) => {
    let errorString = '';
    Object.keys(errors).forEach((key) => {
      const value = errors[key];
      errorString += parseByDelimiter(key).toString() + ' ' + value + '\n';
    });
    return errorString;
  };

  // Focus on next field
  const handleFocusNextField = useCallback((nextField, index, length) => {
    const currentRef = inputRefs.current[nextField];
    if (currentRef && index < length - 1) {
      currentRef.current?.focus();
    } else {
      Keyboard.dismiss();
    }
  }, [inputRefs]);

  // Set input reference
  const setInputRef = (fieldName, ref) => {
    inputRefs.current[fieldName] = ref;
  };

  const validateForm = useCallback(async () => {
    const currentFormikRef = ref as React.MutableRefObject<FormikProps<any>>;
    if (currentFormikRef?.current) {
      return await currentFormikRef.current.validateForm();
    }
    return {};
  }, [ref]);

  // Don't wrap in useMemo - it causes remounts when dependencies change during initialization
  const theme = themeSelector();
  
  return (
    <View style={[{
      backgroundColor: 'transparent',
      flex: 1,
      minHeight: Platform.OS !== 'web' ? "80%" : undefined,
      minWidth: '100%',
      alignSelf: "stretch",
      paddingBottom: props.addPadding ? 250 : undefined
    }, props.customStyling]}>
      {(loading || schemaArray.length === 0) && (
        <View style={{
          flex: 1,
          justifyContent: 'center',
          alignItems: 'center',
          paddingVertical: 60,
          paddingHorizontal: 20,
          minHeight: 300
        }}>
          <View style={{
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24,
            borderRadius: 16,
            backgroundColor: theme ? 'rgba(255, 255, 255, 0.9)' : 'rgba(30, 41, 59, 0.9)',
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 2 },
            shadowOpacity: 0.1,
            shadowRadius: 8,
            elevation: 3,
            minWidth: 200
          }}>
            <Spinner size="large" color={theme ? '#3b82f6' : '#60a5fa'} />
            <Text style={{
              marginTop: 20,
              fontSize: 16,
              fontWeight: '600',
              color: theme ? '#1f2937' : '#f9fafb',
              textAlign: 'center'
            }}>
              Loading form fields...
            </Text>
            <Text style={{
              marginTop: 8,
              fontSize: 13,
              color: theme ? '#6b7280' : '#9ca3af',
              textAlign: 'center',
              maxWidth: 250
            }}>
              Please wait while we prepare your form
            </Text>
          </View>
        </View>
      )}
      {!loading && schemaArray.length > 0 && (
        <Formik
          initialValues={initialValues}
          innerRef={ref}
          enableReinitialize={false}
          validateOnChange={false} // Disable validation on change to prevent re-renders that cause keyboard dismissal
          validateOnBlur={Platform.OS === 'web' && isTouchDeviceRef.current ? false : true} // Disable blur validation on web touch devices to prevent form remounting
          validateOnMount={false}
          validate={conditionalValidate} // Use conditional validation that filters hidden fields
          validationSchema={undefined} // Don't use validationSchema when we have validate function
          onSubmit={async (values, helpers) => {
          console.log("=".repeat(80));
          console.log("🔄 [GenerateSchemaForm] ⭐ Formik onSubmit HANDLER CALLED ⭐");
          console.log("=".repeat(80));
          console.log("📝 Form Values:", JSON.stringify(values, null, 2));
          console.log("🔍 Formik helpers:", {
            isValid: helpers.isValid,
            isValidating: helpers.isValidating,
            submitCount: helpers.submitCount
          });
          try {
            // Step 0: Validate form before submission
            console.log("🔍 [GenerateSchemaForm] Running validation...");
            const allValidationErrors = await validateForm();
            console.log("✅ [GenerateSchemaForm] Raw validation result:", allValidationErrors);
            
            // CRITICAL: Filter out validation errors for hidden/conditional fields
            const visibleFields = getVisibleFields(values || {});
            const visibleFieldNames = new Set(visibleFields.map(f => f.title || f.key));
            console.log("👀 Visible fields:", Array.from(visibleFieldNames));
            
            const validationErrors: any = {};
            if (allValidationErrors) {
              Object.keys(allValidationErrors).forEach(fieldName => {
                if (visibleFieldNames.has(fieldName)) {
                  // Only include errors for visible fields
                  validationErrors[fieldName] = allValidationErrors[fieldName];
                } else {
                  console.log(`⏭️  Skipping validation error for hidden field: ${fieldName}`);
                }
              });
            }
            
            if (validationErrors && Object.keys(validationErrors).length > 0) {
              console.error("❌ [GenerateSchemaForm] VALIDATION FAILED - Blocking submission");
              console.error("📊 Validation errors (visible fields):", JSON.stringify(validationErrors, null, 2));
              helpers.setTouched(setNestedObjectValues<FormikTouched<FormValues>>(validationErrors, true));
              helpers.setStatus({ error: "Submission failed. Please try again." });
              console.log("⚠️ [GenerateSchemaForm] Submission BLOCKED due to validation errors");
              console.log("=".repeat(80));
              return;
            }
            
            // Step 1: Await the submission.
            if (!props.submitFunction) {
              helpers.setStatus({ error: "Submit function not configured" });
              return;
            }
            
            const res = await props.submitFunction({ ...values, ...props.extraValues }, props.other ? combinedIds : id);

            // Step 2: Immediately set the success status.
            // This makes the button show its success state (✅) right away.
            helpers.setStatus({ success: true });

            // Step 3: Schedule all additional functions to run after a 3-second delay.
            // This prevents them from causing conflicting re-renders.
            setTimeout(() => {
              // --- All side-effects are now inside this delay ---
              if (props.extraFunction) {
                props.extraFunction(values);
              }
              if (props.callback) {
                props.callback.setState(prevState => !prevState);
              }
              if (props.sheetRef?.current) {
                props.sheetRef.current.snapToIndex?.(0);
              }
              if (props.extraCallBack) {
                props.extraCallBack(res?.data);
              }
              if (props.resetOnSubmit) {
                helpers.resetForm();
              }

              // Optional: Reset the button's status back to normal after the delay.
              helpers.setStatus(null);

            }, 3000); // 3-second delay

          } catch (error) {
            // --- On Failure ---
            console.error("Submission failed:", error);
            helpers.setStatus({ error: "Submission failed. Please try again." });

            // Clear the error message after a few seconds.
            setTimeout(() => {
              helpers.setStatus(null);
            }, 3000);
          }
        }}
      >
          {(formikProps) => (
            <FormBody
              formikProps={formikProps}
              props={props}
              schemaArray={schemaArray}
              formInstanceId={formInstanceId}
              currentQuestionIndex={currentQuestionIndex}
              setCurrentQuestionIndex={setCurrentQuestionIndex}
              endReachedTriggered={endReachedTriggered}
              handleFocusNextField={handleFocusNextField}
              setInputRef={setInputRef}
              getVisibleFields={getVisibleFields}
              RenderStagedRows={RenderStagedRows}
              RenderRows={RenderRows}
              isDevice={isDevice}
              isTouchDeviceRef={isTouchDeviceRef}
              shouldPreventBlurRef={shouldPreventBlurRef}
              focusTimeRef={focusTimeRef}
              columns={columns}
              inlineFields={props.inlineFields}
            />
          )}
      </Formik>
      )}
    </View>
  );
});


// Utility function to merge styles without border conflicts
const mergeButtonStyles = (baseStyle, customStyle) => {
  if (!customStyle) return baseStyle;

  const merged = { ...baseStyle, ...customStyle };

  // Handle borderRadius conflicts
  if (merged.borderRadius && (
    merged.borderTopLeftRadius ||
    merged.borderTopRightRadius ||
    merged.borderBottomLeftRadius ||
    merged.borderBottomRightRadius
  )) {
    delete merged.borderRadius;
  }

  // Remove any conflicting border properties
  if (merged.borderColor && (merged.borderTopColor || merged.borderRightColor || merged.borderBottomColor || merged.borderLeftColor)) {
    // Keep specific border colors and remove shorthand
    delete merged.borderColor;
  }

  // Handle borderWidth conflicts
  if (merged.borderWidth && (
    merged.borderTopWidth ||
    merged.borderRightWidth ||
    merged.borderBottomWidth ||
    merged.borderLeftWidth
  )) {
    delete merged.borderWidth;
  }

  // Handle borderStyle conflicts
  if (merged.borderStyle && (
    merged.borderTopStyle ||
    merged.borderRightStyle ||
    merged.borderBottomStyle ||
    merged.borderLeftStyle
  )) {
    delete merged.borderStyle;
  }

  return merged;
};

// This is an example of an animatable button component
const Button = ({
  onPress,
  buttonText,
  isLoading,
  isSuccess,
  icon,
  disabled,
  defaultColor = '#3b82f6',
  customStyle
}) => {

  return (
    <MotiPressable
      onPress={onPress}
      disabled={disabled || isLoading}
      style={mergeButtonStyles({
        paddingVertical: 14,
        paddingHorizontal: 32,
        borderRadius: 12,
        justifyContent: 'center',
        alignItems: 'center',
        overflow: 'hidden',
        minHeight: 48,
        borderWidth: 0,
        borderStyle: 'solid',
      }, customStyle)}
      animate={{
        backgroundColor: isSuccess
          ? '#10b981'
          : isLoading
            ? '#9ca3af'
            : disabled
              ? '#d1d5db'
              : defaultColor,
        opacity: disabled ? 0.6 : 1,
        scale: isLoading ? 0.98 : 1,
      }}
      transition={{
        type: 'timing',
        duration: 200,
      }}
      from={{ scale: 1 }}
    >
      {isSuccess ? (
        <Animatable.View
          animation="bounceIn"
          duration={600}
          style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}
        >
          <Animatable.Text
            animation="bounceIn"
            duration={600}
            style={{ color: 'white', fontSize: 18 }}
          >
            ✅
          </Animatable.Text>
          <Text style={{ color: 'white', fontSize: 16, fontWeight: '600' }}>
            Success
          </Text>
        </Animatable.View>
      ) : isLoading ? (
          <Animatable.View
            animation="fadeIn"
            iterationCount="infinite"
            duration={800}
            style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}
          >
            <Spinner size="small" color="white" />
            <Text style={{ color: 'white', fontSize: 16, fontWeight: '600' }}>
              {buttonText}
            </Text>
          </Animatable.View>
      ) : (
            <Animatable.View
              animation="fadeIn"
              duration={200}
              style={{ flexDirection: 'row', alignItems: 'center', gap: icon ? 8 : 0 }}
            >
              {icon && (
                typeof icon === 'string' ? (
                  <Text style={{ color: 'white', fontSize: 18 }}>{icon}</Text>
                ) : (
                  icon
                )
              )}
              <Text style={{
                color: 'white',
                fontSize: 16,
                fontWeight: '600',
                textAlign: 'center'
              }}>
                {buttonText}
              </Text>
            </Animatable.View>
      )}
    </MotiPressable>
  );
};


export default memo(GenerateSchemaForm);
